import { useState, useEffect } from 'react';
import { ArrowLeft, Package, MapPin, Clock, DollarSign, Star, Filter, Calendar, ChevronRight, Loader2 } from 'lucide-react';
import { dbService } from '../../services/firebase-db.service';
import { auth } from '../../config/firebase.config';

interface HistoryScreenProps {
  onBack: () => void;
}

interface DeliveryRecord {
  id: string;
  date: string;
  from: string;
  to: string;
  address: string;
  distance: string;
  earnings: string;
  duration: string;
  rating: number;
  status: string;
}

function msToTime(ms: number): string {
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} mins`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDate(ts: any): string {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const dayDiff = Math.floor((now.getTime() - date.getTime()) / 86400000);
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (dayDiff === 0) return `Today, ${timeStr}`;
  if (dayDiff === 1) return `Yesterday, ${timeStr}`;
  return date.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
}

function isPeriod(ts: any, period: 'day' | 'week' | 'month'): boolean {
  if (!ts) return false;
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const ms = now.getTime() - date.getTime();
  if (period === 'day') return ms < 86400000;
  if (period === 'week') return ms < 7 * 86400000;
  return ms < 30 * 86400000;
}

export function HistoryScreen({ onBack }: HistoryScreenProps) {
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('week');
  const [filterOpen, setFilterOpen] = useState(false);
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [allRaw, setAllRaw] = useState<any[]>([]);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }
    try {
      const raw = await dbService.getRiderJobHistory(user.uid, 100);
      setAllRaw(raw);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filtered = allRaw
      .filter(job => {
        const ts = job.deliveredAt || job.completedAt || job.createdAt;
        return isPeriod(ts, period);
      })
      .map(job => {
        const ts = job.deliveredAt || job.completedAt || job.createdAt;
        const payout = job.paymentBreakdown?.riderEarning ?? job.riderEarning ?? job.earnings ?? 0;
        const tip = job.paymentBreakdown?.tip ?? 0;
        const totalEarned = payout + tip;

        let durationStr = '';
        if (job.deliveredAt && job.createdAt) {
          const start = job.createdAt.toDate ? job.createdAt.toDate() : new Date(job.createdAt);
          const end = job.deliveredAt.toDate ? job.deliveredAt.toDate() : new Date(job.deliveredAt);
          durationStr = msToTime(end.getTime() - start.getTime());
        }

        return {
          id: job.id,
          date: formatDate(ts),
          from: (job.pickupAddress || '').split(',')[0] || 'Pickup',
          to: (job.deliveryAddress || '').split(',')[0] || 'Delivery',
          address: job.deliveryAddress || '',
          distance: job.distance || '—',
          earnings: `₦${totalEarned.toLocaleString()}`,
          duration: durationStr || '—',
          rating: job.rating ?? 5,
          status: job.status || 'completed',
        };
      });
    setDeliveries(filtered);
  }, [allRaw, period]);

  const totalEarnings = deliveries.reduce((sum, d) => {
    return sum + parseFloat(d.earnings.replace(/[₦,]/g, ''));
  }, 0);

  return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={onBack}
            className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-6 h-6 text-[#111111]" />
          </button>
          <div className="flex-1">
            <h1 className="text-[18px] text-[#111111]">Delivery History</h1>
            <p className="text-[12px] text-[#6B6B6B]">All completed deliveries</p>
          </div>
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 rounded-xl transition-colors"
            aria-label="Filter"
          >
            <Filter className="w-5 h-5 text-[#111111]" />
          </button>
        </div>

        {/* Period Tabs */}
        <div className="flex gap-2">
          {(['day', 'week', 'month'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`flex-1 h-10 rounded-[12px] transition-all ${
                period === p
                  ? 'bg-[#E0001B] text-white'
                  : 'bg-gray-100 text-[#6B6B6B] hover:bg-gray-200'
              }`}
            >
              <span className="text-[14px] capitalize">{p}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-[12px] p-3">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-[#E0001B]" />
              <span className="text-[12px] text-[#6B6B6B]">Deliveries</span>
            </div>
            <p className="text-[20px] text-[#111111]">{loading ? '—' : deliveries.length}</p>
          </div>
          <div className="bg-gray-50 rounded-[12px] p-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-[#00B277]" />
              <span className="text-[12px] text-[#6B6B6B]">Earned</span>
            </div>
            <p className="text-[20px] text-[#00B277]">
              {loading ? '—' : `₦${totalEarnings.toLocaleString()}`}
            </p>
          </div>
        </div>
      </div>

      {/* Delivery List */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#E0001B] animate-spin" />
          </div>
        ) : deliveries.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <Package className="w-8 h-8 text-[#6B6B6B]" />
            </div>
            <p className="text-[#111111] mb-1">No deliveries yet</p>
            <p className="text-[14px] text-[#6B6B6B]">
              {period === 'day' ? 'No deliveries today' :
               period === 'week' ? 'No deliveries this week' : 'No deliveries this month'}
            </p>
          </div>
        ) : (
          deliveries.map((delivery) => (
            <div
              key={delivery.id}
              className="bg-white rounded-[16px] p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-[#00B277]/10 flex items-center justify-center">
                    <Package className="w-5 h-5 text-[#00B277]" />
                  </div>
                  <div>
                    <p className="text-[14px] text-[#111111]">{delivery.id.slice(0, 10).toUpperCase()}</p>
                    <p className="text-[12px] text-[#6B6B6B]">{delivery.date}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[16px] text-[#00B277] mb-1">{delivery.earnings}</p>
                  <div className="flex items-center gap-1 justify-end">
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                    <span className="text-[12px] text-[#111111]">{delivery.rating}.0</span>
                  </div>
                </div>
              </div>

              {/* Route */}
              <div className="space-y-2 mb-3">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#E0001B] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[12px] text-[#6B6B6B]">Pickup</p>
                    <p className="text-[14px] text-[#111111]">{delivery.from}</p>
                  </div>
                </div>
                <div className="ml-2 w-0.5 h-4 bg-gray-200" />
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#00B277] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[12px] text-[#6B6B6B]">Delivery</p>
                    <p className="text-[14px] text-[#111111]">{delivery.to}</p>
                    <p className="text-[12px] text-[#6B6B6B] truncate">{delivery.address}</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center gap-4 text-[12px] text-[#6B6B6B]">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {delivery.distance}
                  </div>
                  {delivery.duration !== '—' && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {delivery.duration}
                    </div>
                  )}
                </div>
                <span className="text-[12px] text-[#00B277] font-medium capitalize">{delivery.status}</span>
              </div>
            </div>
          ))
        )}

        {!loading && deliveries.length > 0 && (
          <button
            onClick={loadHistory}
            className="w-full h-12 rounded-[12px] border-2 border-dashed border-gray-300 text-[#6B6B6B] hover:border-[#E0001B] hover:text-[#E0001B] transition-colors"
          >
            Refresh
          </button>
        )}
      </div>

      {/* Filter Modal */}
      {filterOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-[24px] p-4">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <h2 className="text-[18px] text-[#111111] mb-4">Filter History</h2>
            <div className="space-y-4 mb-4">
              <div>
                <label className="block text-[14px] text-[#6B6B6B] mb-2">Period</label>
                <div className="flex gap-2">
                  {(['day', 'week', 'month'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => { setPeriod(p); setFilterOpen(false); }}
                      className={`flex-1 h-10 rounded-[12px] capitalize text-[14px] transition-colors ${
                        period === p ? 'bg-[#E0001B] text-white' : 'bg-gray-100 text-[#6B6B6B]'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={() => setFilterOpen(false)}
              className="w-full h-12 rounded-[12px] bg-gray-100 text-[#111111]"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
